import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import NavbarContext from '../../navbar/context'
import {
  setBackPathname,
  setClosePathname,
  setIsBackButtonVisible,
  setIsCloseButtonVisible,
} from '../../navbar/context/action'
import MainLayout from '../../../shared/layout/main-layout/MainLayout'
import AmountView from './AmountView'
import {
  fetchStatusIs,
  LocationConfig,
  RequestStatus,
} from '../../../api/types'
import locationApi from '../../../api/services/LocationApi'
import {
  setLocationConfig,
  setRules,
  setSelectedGoal,
  setSelectedOrganization,
  setVouchers,
} from '../../context/actions'
import AppContext from '../../context'
import AppSpinner from '../../../components/AppSpinner'

export type AmountParamsType = {
  locationCode: string
  organizationId: string
  goalId: string
}

const Amount = () => {
  const { locationCode, organizationId, goalId } = useParams<AmountParamsType>()

  const { state, contextDispatch } = useContext(AppContext)
  const { contextDispatch: navbarContextDispatch } = useContext(NavbarContext)

  const [requestStatusGoal, setRequestStatusGoal] = useState<RequestStatus>(
    RequestStatus.Idle
  )
  const {
    idle: idleGoal,
    pending: pendingGoal,
    fulfilled: fulfilledGoal,
  } = fetchStatusIs(requestStatusGoal)

  const [requestStatusVouchers, setRequestStatusVouchers] =
    useState<RequestStatus>(RequestStatus.Idle)
  const {
    idle: idleVouchers,
    pending: pendingVouchers,
    fulfilled: fulfilledVouchers,
  } = fetchStatusIs(requestStatusVouchers)

  const [requestStatusRules, setRequestStatusRules] = useState<RequestStatus>(
    RequestStatus.Idle
  )
  const {
    idle: idleRules,
    pending: pendingRules,
    fulfilled: fulfilledRules,
  } = fetchStatusIs(requestStatusRules)

  useEffect(() => {
    setBackPathname(navbarContextDispatch, `/${locationCode}/${organizationId}`)
    setClosePathname(navbarContextDispatch, `/${locationCode}`)
    setIsBackButtonVisible(navbarContextDispatch, true)
    setIsCloseButtonVisible(navbarContextDispatch, true)
  }, [locationCode, organizationId, navbarContextDispatch])

  const fetchVouchers = useCallback(async () => {
    if (!locationCode || !organizationId || !goalId) return

    setRequestStatusVouchers(RequestStatus.Pending)
    const result = await locationApi.getVouchers(
      locationCode,
      organizationId,
      goalId
    )

    if (result) {
      setVouchers(contextDispatch, result.data)
      setRequestStatusVouchers(RequestStatus.Fulfilled)
    } else {
      setRequestStatusVouchers(RequestStatus.Rejected)
    }
  }, [contextDispatch, locationCode, organizationId, goalId])

  useEffect(() => {
    if (idleVouchers) fetchVouchers()
  }, [idleVouchers, fetchVouchers])

  const fetchRules = useCallback(async () => {
    if (!locationCode || !organizationId || !goalId) return

    setRequestStatusRules(RequestStatus.Pending)
    const result = await locationApi.getRules(
      locationCode,
      organizationId,
      goalId
    )

    if (result) {
      setRules(contextDispatch, result.data)
      setRequestStatusRules(RequestStatus.Fulfilled)
    } else {
      setRequestStatusRules(RequestStatus.Rejected)
    }
  }, [contextDispatch, locationCode, organizationId, goalId])

  useEffect(() => {
    if (idleRules) fetchRules()
  }, [idleRules, fetchRules])

  const fetch = useCallback(async () => {
    if (!locationCode || !organizationId || !goalId) return

    setRequestStatusGoal(RequestStatus.Pending)
    const result = await locationApi.getGoal(
      locationCode,
      organizationId,
      goalId
    )

    if (result) {
      setSelectedOrganization(contextDispatch, result.data.organization)
      setSelectedGoal(contextDispatch, result.data.goal)

      const data: LocationConfig = {
        logoId: result.data.logoId,
        backgroundId: result.data.backgroundId,
        locationCode: locationCode,
      }
      setLocationConfig(contextDispatch, data)
      setRequestStatusGoal(RequestStatus.Fulfilled)
    } else {
      setRequestStatusGoal(RequestStatus.Rejected)
    }
  }, [contextDispatch, locationCode, organizationId, goalId])

  useEffect(() => {
    if (idleGoal)
      if (!state.selectedOrganization || !state.selectedGoal) {
        fetch()
      } else {
        setRequestStatusGoal(RequestStatus.Fulfilled)
      }
  }, [fetch, idleGoal, state.selectedGoal, state.selectedOrganization])

  return (
    <>
      <MainLayout withSideImage withNavbar>
        <AppSpinner show={pendingGoal || pendingVouchers || pendingRules} />
        {fulfilledGoal && fulfilledVouchers && fulfilledRules && <AmountView />}
      </MainLayout>
    </>
  )
}

export default Amount
